import React from 'react'
import { Button, Divider, withStyles } from '@material-ui/core'
import { styles } from 'asterX-UI/Pagination/pagination.styles'
import PropTypes from 'prop-types'
import { PaginationItem, usePagination } from '@material-ui/lab'
import clsx from 'clsx'
import { ArrowLeftIcon, ArrowRightIcon } from 'asterX-UI/Icons'

function defaultGetAriaLabel(type, page, selected) {
  if (type === 'page') {
    return `${selected ? '' : 'Go to '}page ${page}`
  }
  return `Go to ${type} page`
}

const Pagination = React.forwardRef(function Pagination(props, ref) {
  const {
    boundaryCount,
    classes,
    className,
    color = 'standard',
    count,
    isMobile = false,
    defaultPage,
    disabled,
    getItemAriaLabel = defaultGetAriaLabel,
    hideNextButton,
    hidePrevButton,
    onChange,
    page,
    renderItem = item => <PaginationItem {...item} />,
    shape = 'round',
    showFirstButton,
    showLastButton,
    siblingCount,
    size = 'medium',
    variant = 'text',
    ...other
  } = props

  const { items } = usePagination({
    ...props,
    componentName: 'Pagination'
  })
  // "previous" next
  const { previous, next } = items
    ?.filter(item => item.type === 'previous' || item.type === 'next')
    ?.reduce((obj, cur) => ({ ...obj, [cur.type]: cur }), {})

  const otherItems = items?.filter(
    item => item.type !== 'previous' && item.type !== 'next'
  )
  return (
    <div>
      <Divider variant={'fullWidth'} className={classes.divider} />
      <nav
        aria-label="pagination navigation"
        className={clsx(classes.root, className)}
        ref={ref}
        {...other}
      >
        <div>
          {isMobile ? (
            <PaginationItem {...previous} variant="outlined" shape="rounded" />
          ) : (
            <Button {...previous} className={classes.previousButton}>
              {' '}
              <ArrowLeftIcon />
              Предыдущая
            </Button>
          )}
        </div>
        {isMobile ? (
          <div className={classes.countPages}>{`Страница ${page || 0} из ${
            count || 0
          } `}</div>
        ) : (
          <ul className={classes.ul}>
            {otherItems.map((item, index) => (
              <li key={index}>
                {renderItem({
                  ...item,
                  color,
                  'aria-label': getItemAriaLabel(
                    item.type,
                    item.page,
                    item.selected
                  ),
                  shape,
                  size: 'large',
                  variant
                })}
              </li>
            ))}
          </ul>
        )}
        <div>
          {isMobile ? (
            <PaginationItem {...next} variant="outlined" shape="rounded" ic />
          ) : (
            <Button {...next} className={classes.nextButton}>
              Следующая <ArrowRightIcon />
            </Button>
          )}
        </div>
      </nav>
    </div>
  )
})

// @default tags synced with default values from usePagination

Pagination.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------
  /**
   * Number of always visible pages at the beginning and end.
   * @default 1
   */
  boundaryCount: PropTypes.number,
  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The active color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'standard']),
  /**
   * The total number of pages.
   * @default 1
   */
  count: PropTypes.number,
  /**
   * The page selected by default when the component is uncontrolled.
   * @default 1
   */
  defaultPage: PropTypes.number,
  /**
   * If `true`, the pagination component will be disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the pagination component will be to mobile view.
   * @default false
   */
  isMobile: PropTypes.bool,
  /**
   * Accepts a function which returns a string value that provides a user-friendly name for the current page.
   *
   * For localization purposes, you can use the provided [translations](/guides/localization/).
   *
   * @param {string} type The link or button type to format ('page' | 'first' | 'last' | 'next' | 'previous'). Defaults to 'page'.
   * @param {number} page The page number to format.
   * @param {bool} selected If true, the current page is selected.
   * @returns {string}
   */
  getItemAriaLabel: PropTypes.func,
  /**
   * If `true`, hide the next-page button.
   * @default false
   */
  hideNextButton: PropTypes.bool,
  /**
   * If `true`, hide the previous-page button.
   * @default false
   */
  hidePrevButton: PropTypes.bool,
  /**
   * Callback fired when the page is changed.
   *
   * @param {object} event The event source of the callback.
   * @param {number} page The page selected.
   */
  onChange: PropTypes.func,
  /**
   * The current page.
   */
  page: PropTypes.number,
  /**
   * Render the item.
   *
   * @param {PaginationRenderItemParams} params The props to spread on a PaginationItem.
   * @returns {ReactNode}
   */
  renderItem: PropTypes.func,
  /**
   * The shape of the pagination items.
   */
  shape: PropTypes.oneOf(['round', 'rounded']),
  /**
   * If `true`, show the first-page button.
   * @default false
   */
  showFirstButton: PropTypes.bool,
  /**
   * If `true`, show the last-page button.
   * @default false
   */
  showLastButton: PropTypes.bool,
  /**
   * Number of always visible pages before and after the current page.
   * @default 1
   */
  siblingCount: PropTypes.number,
  /**
   * The size of the pagination component.
   */
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  variant: PropTypes.oneOf(['outlined', 'text'])
}

export default withStyles(styles, { name: 'MuiPagination' })(Pagination)
